import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { api, ApiError } from "../api/client";
import type { MeResponse, MailboxSummary, UserPrefs } from "../../shared/types";

interface AppState {
  status: "loading" | "signed_out" | "signed_in";
  me: MeResponse | null;
  currentMailbox: MailboxSummary | null;
  setCurrentMailboxId: (id: string) => void;
  refresh: () => Promise<void>;
  updatePrefs: (patch: Partial<UserPrefs>) => Promise<void>;
  signOut: () => Promise<void>;
}

const AppCtx = createContext<AppState | null>(null);

const LAST_MAILBOX_KEY = "gm.lastMailboxId";

export function AppProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [status, setStatus] = useState<AppState["status"]>("loading");
  const [currentMailboxId, setCurrentMailboxIdState] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get<MeResponse>("/api/me");
      setMe(res);
      setStatus("signed_in");
      setCurrentMailboxIdState((prev) => {
        if (prev && res.mailboxes.some((m) => m.id === prev)) return prev;
        const stored = localStorage.getItem(LAST_MAILBOX_KEY);
        if (stored && res.mailboxes.some((m) => m.id === stored)) return stored;
        const shared = res.mailboxes.find((m) => m.kind === "shared");
        return (shared ?? res.mailboxes[0])?.id ?? null;
      });
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setMe(null);
        setStatus("signed_out");
      } else {
        throw e;
      }
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setCurrentMailboxId = useCallback((id: string) => {
    setCurrentMailboxIdState(id);
    localStorage.setItem(LAST_MAILBOX_KEY, id);
  }, []);

  const updatePrefs = useCallback(async (patch: Partial<UserPrefs>) => {
    const next = await api.patch<UserPrefs>("/api/me/prefs", patch);
    setMe((prev) => (prev ? { ...prev, prefs: next } : prev));
  }, []);

  const signOut = useCallback(async () => {
    await api.post("/auth/logout");
    setMe(null);
    setStatus("signed_out");
  }, []);

  const currentMailbox = useMemo(
    () => me?.mailboxes.find((m) => m.id === currentMailboxId) ?? null,
    [me, currentMailboxId],
  );

  const value: AppState = { status, me, currentMailbox, setCurrentMailboxId, refresh: load, updatePrefs, signOut };
  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}

export function useApp(): AppState {
  const ctx = useContext(AppCtx);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}
